import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Pagination,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Description as MixIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { dropdownService } from '../../services/dropdownService';
import { DropdownOption } from '../../types/dropdown';
import PageLayout from '../../components/Layout/PageLayout';
import BackButton from '../../components/BackButton';
import { MixID } from '../../types/mixID';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableSort, useTableSort2 } from '../../hooks/useTableSort';
import SortableTableHead from '../../components/Table/SortableTableHead';
import {
  getMixingPlantLocationFullName,
  getConcreteSupplierFullName,
  getConcreteSupplierFullNameWithoutCode,
  getMixingPlantLocationFullNameWithoutCode,
  getCementTypeFullName,
  getCementSourceFullName,
  getPfaSourceFullName,
  getGgbsSourceFullName,
  getAggregateSourceFullName,
  getAggregateTypeFullName,
  getAdmixtureSourceFullName
} from '../../utils/dropdownUtils';
import { useRole } from '../../hooks/useRole';

const MixIDPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAdminOrManager } = useRole();
  const [mixIDs, setMixIDs] = useState<MixID[]>([]);
  const [loading, setLoading] = useState(false);

  // 表格排序功能
  const getValue = (item: MixID, key: string) => {
    switch (key) {
      case 'mixID':
        return item.MIX_ID;
      case 'grade':
        return item.GRADE_TEXT;
      case 'designedSlump':
        return item.DESIGNED_SLUMP;
      case 'concreteSupplier':
        return item.CONCRETE_SUPPLIER || '';
      case 'mixingPlantLocation':
        return item.MIXING_PLANT_LOC || '';
      default:
        return '';
    }
  };

  //const { sortConfig, handleSort, sortedData } = useTableSort(mixIDs, getValue);
  const { sortConfig, handleSort} = useTableSort2();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMixID, setEditingMixID] = useState<MixID | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterPlant, setFilterPlant] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 下拉選單數據狀態
  const [concreteSuppliers, setConcreteSuppliers] = useState<DropdownOption[]>([]);
  const [mixingPlantLocations, setMixingPlantLocations] = useState<DropdownOption[]>([]);
  const [cementType, setCementType] = useState<DropdownOption[]>([]);
  const [cementSources, setCementSources] = useState<DropdownOption[]>([]);
  const [pfaSources, setPfaSources] = useState<DropdownOption[]>([]);
  const [ggbsSources, setGGBSSources] = useState<DropdownOption[]>([]);
  const [aggregateSources, setAggregateSources] = useState<DropdownOption[]>([]);
  const [admixtureSources, setAdmixtureSources] = useState<DropdownOption[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    MIX_ID: '',
    CEDD_MIX_ID: '',
    GRADE_TEXT: '',
    DESIGNED_SLUMP: '',
    DESIGNED_FLOW_VALUE: '',
    DESIGNED_SLUMP_FLOW: '',
    T_500: '',
    T_END_OF_FLOW: '',
    USAGE: '',
    CEMENT_CONTENT: '',
    CEMENT_TYPE: '',
    CEMENT_TYPE_TEXT: '',
    CEMENT_SOURCE: '',
    CEMENT_SOURCE_TEXT: '',
    PFA_CONTENT: '',
    PFA_TYPE: '',
    PFA_TYPE_TEXT: '',
    PFA_SOURCE: '',
    PFA_SOURCE_TEXT: '',
    GGBS_CONTENT: '',
    GGBS_TYPE: '',
    GGBS_TYPE_TEXT: '',
    GGBS_SOURCE: '',
    GGBS_SOURCE_TEXT: '',
    AGG_40_CONTENT: '',
    AGG_40_TYPE: '',
    AGG_40_TYPE_TEXT: '',
    AGG_40_SOURCE: '',
    AGG_40_SOURCE_TEXT: '',
    AGG_20_CONTENT: '',
    AGG_20_TYPE: '',
    AGG_20_TYPE_TEXT: '',
    AGG_20_SOURCE: '',
    AGG_20_SOURCE_TEXT: '',
    AGG_10_CONTENT: '',
    AGG_10_TYPE: '',
    AGG_10_TYPE_TEXT: '',
    AGG_10_SOURCE: '',
    AGG_10_SOURCE_TEXT: '',
    AGG_FINE_CONTENT: '',
    AGG_FINE_TYPE: '',
    AGG_FINE_TYPE_TEXT: '',
    AGG_FINE_SOURCE: '',
    AGG_FINE_SOURCE_TEXT: '',
    AGG_RS_CONTENT: '',
    AGG_RS_TYPE: '',
    AGG_RS_TYPE_TEXT: '',
    AGG_RS_SOURCE: '',
    AGG_RS_SOURCE_TEXT: '',
    ADMIXTURE1_CONTENT: '',
    ADMIXTURE1_TYPE: '',
    ADMIXTURE1_TYPE_TEXT: '',
    ADMIXTURE1_SOURCE: '',
    ADMIXTURE1_SOURCE_TEXT: '',
    ADMIXTURE2_CONTENT: '',
    ADMIXTURE2_TYPE: '',
    ADMIXTURE2_TYPE_TEXT: '',
    ADMIXTURE2_SOURCE: '',
    ADMIXTURE2_SOURCE_TEXT: '',
    ADMIXTURE3_CONTENT: '',
    ADMIXTURE3_TYPE: '',
    ADMIXTURE3_TYPE_TEXT: '',
    ADMIXTURE3_SOURCE: '',
    ADMIXTURE3_SOURCE_TEXT: '',
    ADMIXTURE4_CONTENT: '',
    ADMIXTURE4_TYPE: '',
    ADMIXTURE4_TYPE_TEXT: '',
    ADMIXTURE4_SOURCE: '',
    ADMIXTURE4_SOURCE_TEXT: '',
    ADMIXTURE5_CONTENT: '',
    ADMIXTURE5_TYPE: '',
    ADMIXTURE5_TYPE_TEXT: '',
    ADMIXTURE5_SOURCE: '',
    ADMIXTURE5_SOURCE_TEXT: '',
    PFA_PCT: '',
    FRESH_WATER_VOL: '',
    RECYC_WATER_VOL: '',
    AC_RATIO: '',
    WC_RATIO: '',
    AAR_VALUE: '',
    CONCRETE_SUPPLIER: '',
    CONCRETE_SUPPLIER_CODE: '',
    MIXING_PLANT_LOCATION: '',
    MIX_PLANT_CODE: '',
    ADMIXTURE_UNIT: '',
    DISPLAY_UNIT: '',
  });

  // 載入下拉選單數據
  const loadDropdownData = async () => {
    try {
      setDropdownLoading(true);
      const [suppliers, plants, sources] = await Promise.all([
        dropdownService.getConcreteSuppliers(),
        dropdownService.getMixingPlantLocations(),
        dropdownService.getMaterialSources()
      ]);

      setConcreteSuppliers(suppliers);
      setMixingPlantLocations(plants);
      setCementType(sources.cementType);
      setCementSources(sources.cementSources);
      setPfaSources(sources.pfaSources);
      setGGBSSources(sources.ggbsSources);
      setAggregateSources(sources.aggregateSources);
      setAdmixtureSources(sources.admixtureSources);
      //setConPfaSources(sources);
    } catch (error) {
      console.error('載入下拉選單數據失敗:', error);
      showSnackbar(t('pages.mixID.messages.loadDropdownDataFailed'), 'error');
    } finally {
      setDropdownLoading(false);
    }
  };

  useEffect(() => {
    fetchMixIDs();
    loadDropdownData();
  }, [currentPage]); // 依賴 currentPage 以重新獲取數據

  useEffect(() => {
      setCurrentPage(1);
      fetchMixIDs(false); // 把最新的排序狀態直接傳給 fetchMixIDs
  }, [sortConfig]);

  const fetchMixIDs = async (isloading: boolean = true) => {
    try {
      if(isloading)
        setLoading(true);
      
      const response = await apiService.getMixID({
        MixID: searchTerm,
        Page: currentPage,
        PageSize: pageSize,
        SortBy: sortConfig.direction　=== "none" ? '' : sortConfig.key,         // 新增
        SortDirection: sortConfig.direction === "none" ? '' : sortConfig.direction,  // 新增
      });
      setMixIDs(response.mixIDs);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);

    } catch (error) {
      showSnackbar(t('pages.mixID.messages.getMixIDDataFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 填充source_text和type_text字段的函數
  const populateTextFields = (data: any) => {
    const updatedData = { ...data };

    // 填充水泥相關字段
    if (data.CEMENT_TYPE) {
      updatedData.CEMENT_TYPE_TEXT = getCementTypeFullName(data.CEMENT_TYPE, cementType);
    }
    if (data.CEMENT_SOURCE) {
      updatedData.CEMENT_SOURCE_TEXT = getCementSourceFullName(data.CEMENT_SOURCE, cementSources);
    }

    // 填充PFA相關字段
    if (data.PFA_SOURCE) {
      updatedData.PFA_SOURCE_TEXT = getPfaSourceFullName(data.PFA_SOURCE, pfaSources);
    }

    // 填充GGBS相關字段
    if (data.GGBS_SOURCE) {
      updatedData.GGBS_SOURCE_TEXT = getGgbsSourceFullName(data.GGBS_SOURCE, ggbsSources);
    }

    // 填充骨料相關字段
    if (data.AGG_40_SOURCE) {
      updatedData.AGG_40_SOURCE_TEXT = getAggregateSourceFullName(data.AGG_40_SOURCE, aggregateSources);
    }
    if (data.AGG_20_SOURCE) {
      updatedData.AGG_20_SOURCE_TEXT = getAggregateSourceFullName(data.AGG_20_SOURCE, aggregateSources);
    }
    if (data.AGG_10_SOURCE) {
      updatedData.AGG_10_SOURCE_TEXT = getAggregateSourceFullName(data.AGG_10_SOURCE, aggregateSources);
    }
    if (data.AGG_FINE_SOURCE) {
      updatedData.AGG_FINE_SOURCE_TEXT = getAggregateSourceFullName(data.AGG_FINE_SOURCE, aggregateSources);
    }
    if (data.AGG_RS_SOURCE) {
      updatedData.AGG_RS_SOURCE_TEXT = getAggregateSourceFullName(data.AGG_RS_SOURCE, aggregateSources);
    }

    // 填充外加劑相關字段
    if (data.ADMIXTURE1_SOURCE) {
      updatedData.ADMIXTURE1_SOURCE_TEXT = getAdmixtureSourceFullName(data.ADMIXTURE1_SOURCE, admixtureSources);
    }
    if (data.ADMIXTURE2_SOURCE) {
      updatedData.ADMIXTURE2_SOURCE_TEXT = getAdmixtureSourceFullName(data.ADMIXTURE2_SOURCE, admixtureSources);
    }
    if (data.ADMIXTURE3_SOURCE) {
      updatedData.ADMIXTURE3_SOURCE_TEXT = getAdmixtureSourceFullName(data.ADMIXTURE3_SOURCE, admixtureSources);
    }
    if (data.ADMIXTURE4_SOURCE) {
      updatedData.ADMIXTURE4_SOURCE_TEXT = getAdmixtureSourceFullName(data.ADMIXTURE4_SOURCE, admixtureSources);
    }
    if (data.ADMIXTURE5_SOURCE) {
      updatedData.ADMIXTURE5_SOURCE_TEXT = getAdmixtureSourceFullName(data.ADMIXTURE5_SOURCE, admixtureSources);
    }

    return updatedData;
  };

  const handleSubmit = async () => {
    try {
      // 填充text字段
      const dataWithTextFields = populateTextFields(formData);

      if (editingMixID) {
        await apiService.put(`/mixid/${editingMixID.MIX_ID}`, dataWithTextFields);
        showSnackbar(t('pages.mixID.messages.updateSuccess'), 'success');
      } else {
        await apiService.post('/mixid', dataWithTextFields);
        showSnackbar(t('pages.mixID.messages.createSuccess'), 'success');
      }
      setOpenDialog(false);
      resetForm();
      fetchMixIDs();
    } catch (error) {
      console.log('error', error);
      showSnackbar(t('pages.mixID.messages.operationFailed'), 'error');
    }
  };

  const handleEdit = (mixID: MixID) => {
    setEditingMixID(mixID);
    setFormData({
      MIX_ID: mixID.MIX_ID,
      CEDD_MIX_ID: mixID.CEDD_MIX_ID,
      GRADE_TEXT: mixID.GRADE_TEXT,
      DESIGNED_SLUMP: mixID.DESIGNED_SLUMP,
      DESIGNED_FLOW_VALUE: mixID.DESIGNED_FLOW_VALUE,
      DESIGNED_SLUMP_FLOW: mixID.DESIGNED_SLUMP_FLOW,
      T_500: mixID.T_500,
      T_END_OF_FLOW: mixID.T_END_OF_FLOW,
      USAGE: mixID.USAGE,
      CEMENT_CONTENT: mixID.CEMENT_CONTENT,
      CEMENT_TYPE: mixID.CEMENT_TYPE,
      CEMENT_TYPE_TEXT: mixID.CEMENT_TYPE_TEXT || '',
      CEMENT_SOURCE: mixID.CEMENT_SOURCE,
      CEMENT_SOURCE_TEXT: mixID.CEMENT_SOURCE_TEXT || '',
      PFA_CONTENT: mixID.PFA_CONTENT,
      PFA_TYPE: mixID.PFA_TYPE,
      PFA_TYPE_TEXT: mixID.PFA_TYPE_TEXT || '',
      PFA_SOURCE: mixID.PFA_SOURCE,
      PFA_SOURCE_TEXT: mixID.PFA_SOURCE_TEXT || '',
      GGBS_CONTENT: mixID.GGBS_CONTENT,
      GGBS_TYPE: mixID.GGBS_TYPE,
      GGBS_TYPE_TEXT: mixID.GGBS_TYPE_TEXT || '',
      GGBS_SOURCE: mixID.GGBS_SOURCE,
      GGBS_SOURCE_TEXT: mixID.GGBS_SOURCE_TEXT || '',
      AGG_40_CONTENT: mixID.AGG_40_CONTENT,
      AGG_40_TYPE: mixID.AGG_40_TYPE,
      AGG_40_TYPE_TEXT: mixID.AGG_40_TYPE_TEXT || '',
      AGG_40_SOURCE: mixID.AGG_40_SOURCE,
      AGG_40_SOURCE_TEXT: mixID.AGG_40_SOURCE_TEXT || '',
      AGG_20_CONTENT: mixID.AGG_20_CONTENT,
      AGG_20_TYPE: mixID.AGG_20_TYPE,
      AGG_20_TYPE_TEXT: mixID.AGG_20_TYPE_TEXT || '',
      AGG_20_SOURCE: mixID.AGG_20_SOURCE,
      AGG_20_SOURCE_TEXT: mixID.AGG_20_SOURCE_TEXT || '',
      AGG_10_CONTENT: mixID.AGG_10_CONTENT,
      AGG_10_TYPE: mixID.AGG_10_TYPE,
      AGG_10_TYPE_TEXT: mixID.AGG_10_TYPE_TEXT || '',
      AGG_10_SOURCE: mixID.AGG_10_SOURCE,
      AGG_10_SOURCE_TEXT: mixID.AGG_10_SOURCE_TEXT || '',
      AGG_FINE_CONTENT: mixID.AGG_FINE_CONTENT,
      AGG_FINE_TYPE: mixID.AGG_FINE_TYPE,
      AGG_FINE_TYPE_TEXT: mixID.AGG_FINE_TYPE_TEXT || '',
      AGG_FINE_SOURCE: mixID.AGG_FINE_SOURCE,
      AGG_FINE_SOURCE_TEXT: mixID.AGG_FINE_SOURCE_TEXT || '',
      AGG_RS_CONTENT: mixID.AGG_RS_CONTENT,
      AGG_RS_TYPE: mixID.AGG_RS_TYPE,
      AGG_RS_TYPE_TEXT: mixID.AGG_RS_TYPE_TEXT || '',
      AGG_RS_SOURCE: mixID.AGG_RS_SOURCE,
      AGG_RS_SOURCE_TEXT: mixID.AGG_RS_SOURCE_TEXT || '',
      ADMIXTURE1_CONTENT: mixID.ADMIXTURE1_CONTENT,
      ADMIXTURE1_TYPE: mixID.ADMIXTURE1_TYPE,
      ADMIXTURE1_TYPE_TEXT: mixID.ADMIXTURE1_TYPE_TEXT || '',
      ADMIXTURE1_SOURCE: mixID.ADMIXTURE1_SOURCE,
      ADMIXTURE1_SOURCE_TEXT: mixID.ADMIXTURE1_SOURCE_TEXT || '',
      ADMIXTURE2_CONTENT: mixID.ADMIXTURE2_CONTENT,
      ADMIXTURE2_TYPE: mixID.ADMIXTURE2_TYPE,
      ADMIXTURE2_TYPE_TEXT: mixID.ADMIXTURE2_TYPE_TEXT || '',
      ADMIXTURE2_SOURCE: mixID.ADMIXTURE2_SOURCE,
      ADMIXTURE2_SOURCE_TEXT: mixID.ADMIXTURE2_SOURCE_TEXT || '',
      ADMIXTURE3_CONTENT: mixID.ADMIXTURE3_CONTENT || '',
      ADMIXTURE3_TYPE: mixID.ADMIXTURE3_TYPE || '',
      ADMIXTURE3_TYPE_TEXT: mixID.ADMIXTURE3_TYPE_TEXT || '',
      ADMIXTURE3_SOURCE: mixID.ADMIXTURE3_SOURCE || '',
      ADMIXTURE3_SOURCE_TEXT: mixID.ADMIXTURE3_SOURCE_TEXT || '',
      ADMIXTURE4_CONTENT: mixID.ADMIXTURE4_CONTENT || '',
      ADMIXTURE4_TYPE: mixID.ADMIXTURE4_TYPE || '',
      ADMIXTURE4_TYPE_TEXT: mixID.ADMIXTURE4_TYPE_TEXT || '',
      ADMIXTURE4_SOURCE: mixID.ADMIXTURE4_SOURCE || '',
      ADMIXTURE4_SOURCE_TEXT: mixID.ADMIXTURE4_SOURCE_TEXT || '',
      ADMIXTURE5_CONTENT: mixID.ADMIXTURE5_CONTENT || '',
      ADMIXTURE5_TYPE: mixID.ADMIXTURE5_TYPE || '',
      ADMIXTURE5_TYPE_TEXT: mixID.ADMIXTURE5_TYPE_TEXT || '',
      ADMIXTURE5_SOURCE: mixID.ADMIXTURE5_SOURCE || '',
      ADMIXTURE5_SOURCE_TEXT: mixID.ADMIXTURE5_SOURCE_TEXT || '',
      PFA_PCT: mixID.PFA_PCT,
      FRESH_WATER_VOL: mixID.FRESH_WATER_VOL,
      RECYC_WATER_VOL: mixID.RECYC_WATER_VOL,
      AC_RATIO: mixID.AC_RATIO,
      WC_RATIO: mixID.WC_RATIO,
      AAR_VALUE: mixID.AAR_VALUE,
      CONCRETE_SUPPLIER: mixID.CONCRETE_SUPPLIER,
      CONCRETE_SUPPLIER_CODE: mixID.CONCRETE_SUPPLIER, //Backend didn't return CONCRETE_SUPPLIER_CODE, so use CONCRETE_SUPPLIER,
      MIXING_PLANT_LOCATION: mixID.MIXING_PLANT_LOC,
      MIX_PLANT_CODE: mixID.MIXING_PLANT_LOC,
      ADMIXTURE_UNIT: mixID.ADMIXTURE_UNIT,
      DISPLAY_UNIT: mixID.DISPLAY_UNIT || '',
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      MIX_ID: '',
      CEDD_MIX_ID: '',
      GRADE_TEXT: '',
      DESIGNED_SLUMP: '',
      DESIGNED_FLOW_VALUE: '',
      DESIGNED_SLUMP_FLOW: '',
      T_500: '',
      T_END_OF_FLOW: '',
      USAGE: '',
      CEMENT_CONTENT: '',
      CEMENT_TYPE: '',
      CEMENT_TYPE_TEXT: '',
      CEMENT_SOURCE: '',
      CEMENT_SOURCE_TEXT: '',
      PFA_CONTENT: '',
      PFA_TYPE: '',
      PFA_TYPE_TEXT: '',
      PFA_SOURCE: '',
      PFA_SOURCE_TEXT: '',
      GGBS_CONTENT: '',
      GGBS_TYPE: '',
      GGBS_TYPE_TEXT: '',
      GGBS_SOURCE: '',
      GGBS_SOURCE_TEXT: '',
      AGG_40_CONTENT: '',
      AGG_40_TYPE: '',
      AGG_40_TYPE_TEXT: '',
      AGG_40_SOURCE: '',
      AGG_40_SOURCE_TEXT: '',
      AGG_20_CONTENT: '',
      AGG_20_TYPE: '',
      AGG_20_TYPE_TEXT: '',
      AGG_20_SOURCE: '',
      AGG_20_SOURCE_TEXT: '',
      AGG_10_CONTENT: '',
      AGG_10_TYPE: '',
      AGG_10_TYPE_TEXT: '',
      AGG_10_SOURCE: '',
      AGG_10_SOURCE_TEXT: '',
      AGG_FINE_CONTENT: '',
      AGG_FINE_TYPE: '',
      AGG_FINE_TYPE_TEXT: '',
      AGG_FINE_SOURCE: '',
      AGG_FINE_SOURCE_TEXT: '',
      AGG_RS_CONTENT: '',
      AGG_RS_TYPE: '',
      AGG_RS_TYPE_TEXT: '',
      AGG_RS_SOURCE: '',
      AGG_RS_SOURCE_TEXT: '',
      ADMIXTURE1_CONTENT: '',
      ADMIXTURE1_TYPE: '',
      ADMIXTURE1_TYPE_TEXT: '',
      ADMIXTURE1_SOURCE: '',
      ADMIXTURE1_SOURCE_TEXT: '',
      ADMIXTURE2_CONTENT: '',
      ADMIXTURE2_TYPE: '',
      ADMIXTURE2_TYPE_TEXT: '',
      ADMIXTURE2_SOURCE: '',
      ADMIXTURE2_SOURCE_TEXT: '',
      ADMIXTURE3_CONTENT: '',
      ADMIXTURE3_TYPE: '',
      ADMIXTURE3_TYPE_TEXT: '',
      ADMIXTURE3_SOURCE: '',
      ADMIXTURE3_SOURCE_TEXT: '',
      ADMIXTURE4_CONTENT: '',
      ADMIXTURE4_TYPE: '',
      ADMIXTURE4_TYPE_TEXT: '',
      ADMIXTURE4_SOURCE: '',
      ADMIXTURE4_SOURCE_TEXT: '',
      ADMIXTURE5_CONTENT: '',
      ADMIXTURE5_TYPE: '',
      ADMIXTURE5_TYPE_TEXT: '',
      ADMIXTURE5_SOURCE: '',
      ADMIXTURE5_SOURCE_TEXT: '',
      PFA_PCT: '',
      FRESH_WATER_VOL: '',
      RECYC_WATER_VOL: '',
      AC_RATIO: '',
      WC_RATIO: '',
      AAR_VALUE: '',
      CONCRETE_SUPPLIER: '',
      CONCRETE_SUPPLIER_CODE: '',
      MIXING_PLANT_LOCATION: '',
      MIX_PLANT_CODE: '',
      ADMIXTURE_UNIT: '',
      DISPLAY_UNIT: '',
    });
    setEditingMixID(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <PageLayout title={t('pages.mixID.title')}>
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* 查詢條件 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {t('pages.mixID.queryConditions')}
            </Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
                // disabled={!isAdminOrManager}
              >
                {t('pages.mixID.actions.add')}
              </Button>
              <Button
                sx={{ marginLeft: '10px' }}
                variant="contained"
                onClick={() => {
                  navigate('/mixid/tasks');
                }}
                disabled={!isAdminOrManager}
              >
                {t('pages.mixID.actions.task')}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3} alignItems="center" sx={{ justifyContent: 'space-between' }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('pages.mixID.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    e.preventDefault();
                    fetchMixIDs();
                  }
                }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={() =>fetchMixIDs()}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {loading ? t('pages.mixID.messages.loading') : t('pages.mixID.actions.search')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={(e) => setSearchTerm('')}
                disabled={loading}
              >
                {t('pages.mixID.actions.reset')}
              </Button>
            </Grid>
          </Grid>

          {/* 總計數顯示 */}
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {t('common.queryResult')} <strong style={{ color: '#1976d2' }}>{totalCount}</strong> {t('common.records')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common.pageNumber').replace('{page}', currentPage.toString())}，{t('common.totalPage').replace('{totalPages}', totalPages.toString())}
                  {totalCount > 0 && (
                    t('common.pageSize').replace('{pageSize}', pageSize.toString())
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* 數據表格 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 600,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
              }}
            >
              <Table stickyHeader>
                <SortableTableHead
                  columns={[
                    { key: 'MIX_ID', label: 'MixID' },
                    { key: 'GRADE_TEXT', label: 'Grade Text' },
                    { key: 'DESIGNED_SLUMP', label: 'Designed Slump' },
                    { key: 'CONCRETE_SUPPLIER', label: 'Supplier' },
                    { key: 'MIXING_PLANT_LOC', label: 'Mixing Plant Location' },
                    { key: 'actions', label: t('pages.mixID.table.actions'), sortable: false }
                  ]}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  stickyHeader={true}
                />
                <TableBody>
                  {mixIDs.map((mixID, index) => (
                    <TableRow
                      key={mixID.MIX_ID}
                      sx={{
                        backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          padding: '12px 16px',
                        }
                      }}
                    >
                      <TableCell sx={{
                        fontFamily: 'monospace',
                        fontWeight: 'medium',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MixIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {mixID.MIX_ID}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mixID.GRADE_TEXT}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>{mixID.DESIGNED_SLUMP}</TableCell>
                      <TableCell>
                        {getConcreteSupplierFullNameWithoutCode(mixID.CONCRETE_SUPPLIER, concreteSuppliers)}
                      </TableCell>
                      <TableCell>
                        {getMixingPlantLocationFullNameWithoutCode(mixID.MIXING_PLANT_LOC, mixingPlantLocations)}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('pages.mixID.actions.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(mixID)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white',
                                }
                              }}
                              disabled={!isAdminOrManager}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 分頁 */}
            {totalCount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* 編輯對話框 */}
        <Dialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
            resetForm();
          }}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { minHeight: '80vh' }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              {editingMixID ? '編輯 MixID' : '建立新的 Mix ID 項目'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Box sx={{ mt: 2 }}>
              {/* 頂部區域 - 供應商和廠房 */}
              <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Concrete Supplier:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth>
                      <Select
                        value={formData.CONCRETE_SUPPLIER_CODE}
                        onChange={(e) => setFormData({ ...formData, CONCRETE_SUPPLIER: getConcreteSupplierFullNameWithoutCode(e.target.value, concreteSuppliers), CONCRETE_SUPPLIER_CODE: e.target.value })}
                        disabled={dropdownLoading}
                      >
                        {concreteSuppliers.map((supplier) => (
                          <MenuItem key={supplier.id} value={supplier.LIST_KEY}>
                            {`${supplier.LIST_NAME} - ${supplier.LIST_DESCRIPTION}` || supplier.LIST_KEY}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Mix Plant Location:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth>
                      <Select
                        value={formData.MIX_PLANT_CODE}
                        onChange={(e) => setFormData({ ...formData, MIXING_PLANT_LOCATION: getMixingPlantLocationFullNameWithoutCode(e.target.value, mixingPlantLocations), MIX_PLANT_CODE: e.target.value })}
                        disabled={dropdownLoading}
                      >
                        {mixingPlantLocations.map((plant) => (
                          <MenuItem key={plant.id} value={plant.LIST_KEY}>
                            {`${plant.LIST_KEY} - ${plant.LIST_DESCRIPTION}` || plant.LIST_KEY}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* Mix ID 基本資訊 */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Mix ID 資訊
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Mix ID*:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      //label="Mix ID*"
                      value={formData.MIX_ID}
                      onChange={(e) => setFormData({ ...formData, MIX_ID: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      CEDD Mix ID:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.CEDD_MIX_ID}
                      onChange={(e) => setFormData({ ...formData, CEDD_MIX_ID: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      {t('pages.mixID.form.grade')}:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.GRADE_TEXT}
                      onChange={(e) => setFormData({ ...formData, GRADE_TEXT: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Slump:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.DESIGNED_SLUMP}
                      onChange={(e) => setFormData({ ...formData, DESIGNED_SLUMP: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Flow Value:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.DESIGNED_FLOW_VALUE}
                      onChange={(e) => setFormData({ ...formData, DESIGNED_FLOW_VALUE: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Slump Flow:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.DESIGNED_SLUMP_FLOW}
                      onChange={(e) => setFormData({ ...formData, DESIGNED_SLUMP_FLOW: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      Usage:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.USAGE}
                      onChange={(e) => setFormData({ ...formData, USAGE: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      T500 (sec):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.T_500}
                      onChange={(e) => setFormData({ ...formData, T_500: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      TEnd-of-flow (sec):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.T_END_OF_FLOW}
                      onChange={(e) => setFormData({ ...formData, T_END_OF_FLOW: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* 配比內容表格 */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  配比內容
                </Typography>
                <TableContainer>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.light' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '35%', textAlign: 'left' }}>配比內容</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '32%', textAlign: 'center' }}>類型</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '33%', textAlign: 'center' }}>來源</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* 水泥 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                            <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                              Cement:
                            </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                            <TextField
                              size="small"
                              value={formData.CEMENT_CONTENT}
                              onChange={(e) => setFormData({ ...formData, CEMENT_CONTENT: e.target.value })}
                              InputProps={{ endAdornment: 'kg/m³' }}
                              sx={{ width: '100%' }}
                            />
                            </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.CEMENT_TYPE}
                              onChange={(e) => setFormData({ ...formData, CEMENT_TYPE: e.target.value, CEMENT_TYPE_TEXT: getCementTypeFullName(e.target.value, cementType) })}
                            >
                              {cementType.map((type) => (
                                <MenuItem key={type.id} value={type.LIST_KEY}>
                                  {`${type.LIST_KEY} - ${type.LIST_DESCRIPTION}` || type.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.CEMENT_SOURCE}
                              onChange={(e) => setFormData({ ...formData, CEMENT_SOURCE: e.target.value, CEMENT_SOURCE_TEXT: getCementSourceFullName(e.target.value, cementSources) })}
                            >
                              {cementSources.map((cement) => (
                                <MenuItem key={cement.id} value={cement.LIST_KEY}>
                                  {`${cement.LIST_KEY} - ${cement.LIST_DESCRIPTION}` || cement.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* PFA */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  PFA:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.PFA_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, PFA_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.PFA_TYPE}
                            onChange={(e) => setFormData({ ...formData, PFA_TYPE: e.target.value, PFA_TYPE_TEXT: getCementTypeFullName(e.target.value, cementType) })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.PFA_SOURCE}
                              onChange={(e) => setFormData({ ...formData, PFA_SOURCE: e.target.value, PFA_SOURCE_TEXT: getPfaSourceFullName(e.target.value, pfaSources) })}
                              disabled={dropdownLoading}
                            >
                              {pfaSources.map((pfa) => (
                                <MenuItem key={pfa.id} value={pfa.LIST_KEY}>
                                  {`${pfa.LIST_KEY} - ${pfa.LIST_DESCRIPTION}` || pfa.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* GGBS */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  GGBS:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.GGBS_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, GGBS_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.GGBS_TYPE}
                            onChange={(e) => setFormData({ ...formData, GGBS_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.GGBS_SOURCE}
                              onChange={(e) => setFormData({ ...formData, GGBS_SOURCE: e.target.value, GGBS_SOURCE_TEXT: getGgbsSourceFullName(e.target.value, ggbsSources) })}
                            >
                              {ggbsSources.map((ggbs) => (
                                <MenuItem key={ggbs.id} value={ggbs.LIST_KEY}>
                                  {`${ggbs.LIST_KEY} - ${ggbs.LIST_DESCRIPTION}` || ggbs.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 骨料 (40mm) */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Aggregate (40mm):
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.AGG_40_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, AGG_40_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.AGG_40_TYPE}
                            onChange={(e) => setFormData({ ...formData, AGG_40_TYPE: e.target.value, AGG_40_TYPE_TEXT: getAggregateTypeFullName(e.target.value, aggregateSources) })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.AGG_40_SOURCE}
                              onChange={(e) => setFormData({ ...formData, AGG_40_SOURCE: e.target.value, AGG_40_SOURCE_TEXT: getAggregateSourceFullName(e.target.value, aggregateSources) })}
                            >
                              {aggregateSources.map((agg) => (
                                <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                  {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 骨料 (20mm) */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Aggregate (20mm):
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.AGG_20_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, AGG_20_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.AGG_20_TYPE}
                            onChange={(e) => setFormData({ ...formData, AGG_20_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.AGG_20_SOURCE}
                              onChange={(e) => setFormData({ ...formData, AGG_20_SOURCE: e.target.value })}
                            >
                              {aggregateSources.map((agg) => (
                                <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                  {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 骨料 (10mm) */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Aggregate (10mm):
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.AGG_10_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, AGG_10_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.AGG_10_TYPE}
                            onChange={(e) => setFormData({ ...formData, AGG_10_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.AGG_10_SOURCE}
                              onChange={(e) => setFormData({ ...formData, AGG_10_SOURCE: e.target.value })}
                            >
                              {aggregateSources.map((agg) => (
                                <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                  {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 骨料 (細) */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Aggregate (Fine):
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.AGG_FINE_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, AGG_FINE_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.AGG_FINE_TYPE}
                            onChange={(e) => setFormData({ ...formData, AGG_FINE_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.AGG_FINE_SOURCE}
                              onChange={(e) => setFormData({ ...formData, AGG_FINE_SOURCE: e.target.value })}
                            >
                              {aggregateSources.map((agg) => (
                                <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                  {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 骨料 (河砂) */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Aggregate (River Sand):
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.AGG_RS_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, AGG_RS_CONTENT: e.target.value })}
                                  InputProps={{ endAdornment: 'kg/m³' }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.AGG_RS_TYPE}
                            onChange={(e) => setFormData({ ...formData, AGG_RS_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.AGG_RS_SOURCE}
                              onChange={(e) => setFormData({ ...formData, AGG_RS_SOURCE: e.target.value })}
                            >
                              {aggregateSources.map((agg) => (
                                <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                  {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 外加劑 1 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Admixture 1:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <TextField
                                    size="small"
                                    value={formData.ADMIXTURE1_CONTENT}
                                    onChange={(e) => setFormData({ ...formData, ADMIXTURE1_CONTENT: e.target.value })}
                                    sx={{ flex: 1 }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                      value={formData.ADMIXTURE_UNIT}
                                      onChange={(e) => setFormData({ ...formData, ADMIXTURE_UNIT: e.target.value })}
                                    >
                                      <MenuItem value="L_P_M3">l/m³</MenuItem>
                                      <MenuItem value="KG_P_M3">kg/m³</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.ADMIXTURE1_TYPE}
                            onChange={(e) => setFormData({ ...formData, ADMIXTURE1_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.ADMIXTURE1_SOURCE}
                              onChange={(e) => setFormData({ ...formData, ADMIXTURE1_SOURCE: e.target.value, ADMIXTURE1_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            >
                              {admixtureSources.map((admixture) => (
                                <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                  {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 外加劑 2 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Admixture 2:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.ADMIXTURE2_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, ADMIXTURE2_CONTENT: e.target.value })}
                                  InputProps={{
                                    endAdornment: formData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                  }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.ADMIXTURE2_TYPE}
                            onChange={(e) => setFormData({ ...formData, ADMIXTURE2_TYPE: e.target.value })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.ADMIXTURE2_SOURCE}
                              onChange={(e) => setFormData({ ...formData, ADMIXTURE2_SOURCE: e.target.value, ADMIXTURE2_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            >
                              {admixtureSources.map((admixture) => (
                                <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                  {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 外加劑 3 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Admixture 3:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.ADMIXTURE3_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, ADMIXTURE3_CONTENT: e.target.value })}
                                  InputProps={{
                                    endAdornment: formData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                  }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.ADMIXTURE3_TYPE}
                            onChange={(e) => setFormData({ ...formData, ADMIXTURE3_TYPE: e.target.value, ADMIXTURE3_TYPE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.ADMIXTURE3_SOURCE}
                              onChange={(e) => setFormData({ ...formData, ADMIXTURE3_SOURCE: e.target.value, ADMIXTURE3_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            >
                              {admixtureSources.map((admixture) => (
                                <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                  {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 外加劑 4 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Admixture 4:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.ADMIXTURE4_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, ADMIXTURE4_CONTENT: e.target.value })}
                                  InputProps={{
                                    endAdornment: formData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                  }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.ADMIXTURE4_TYPE}
                            onChange={(e) => setFormData({ ...formData, ADMIXTURE4_TYPE: e.target.value, ADMIXTURE4_TYPE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.ADMIXTURE4_SOURCE}
                              onChange={(e) => setFormData({ ...formData, ADMIXTURE4_SOURCE: e.target.value, ADMIXTURE4_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            >
                              {admixtureSources.map((admixture) => (
                                <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                  {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>

                      {/* 外加劑 5 */}
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                  Admixture 5:
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  size="small"
                                  value={formData.ADMIXTURE5_CONTENT}
                                  onChange={(e) => setFormData({ ...formData, ADMIXTURE5_CONTENT: e.target.value })}
                                  InputProps={{
                                    endAdornment: formData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                  }}
                                  sx={{ width: '100%' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <TextField
                            size="small"
                            value={formData.ADMIXTURE5_TYPE}
                            onChange={(e) => setFormData({ ...formData, ADMIXTURE5_TYPE: e.target.value, ADMIXTURE5_TYPE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            sx={{ width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={formData.ADMIXTURE5_SOURCE}
                              onChange={(e) => setFormData({ ...formData, ADMIXTURE5_SOURCE: e.target.value, ADMIXTURE5_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) })}
                            >
                              {admixtureSources.map((admixture) => (
                                <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                  {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* 比例和水 */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  比例和水
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      % PFA:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.PFA_PCT}
                      onChange={(e) => setFormData({ ...formData, PFA_PCT: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      {t('pages.mixID.form.freshWaterVol')}:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.FRESH_WATER_VOL}
                      onChange={(e) => setFormData({ ...formData, FRESH_WATER_VOL: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      {t('pages.mixID.form.recycWaterVol')}:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.RECYC_WATER_VOL}
                      onChange={(e) => setFormData({ ...formData, RECYC_WATER_VOL: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      {t('pages.mixID.form.acRatio')}:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.AC_RATIO}
                      onChange={(e) => setFormData({ ...formData, AC_RATIO: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      {t('pages.mixID.form.wcRatio')}:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.WC_RATIO}
                      onChange={(e) => setFormData({ ...formData, WC_RATIO: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                      AAR:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      value={formData.AAR_VALUE}
                      onChange={(e) => setFormData({ ...formData, AAR_VALUE: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              size="large"
              sx={{ mr: 2 }}
            >
              {t('common.back')}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              size="large"
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={20} /> : (editingMixID ? t('pages.mixID.actions.save') : t('pages.mixID.actions.save'))}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 通知 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </PageLayout>
  );
};

export default MixIDPage;